import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Briefcase,
  CheckCircle2,
  Layers,
  LifeBuoy,
  MessageSquare,
  Pencil,
  RefreshCw,
  Trash2,
  UserCheck,
  UserCog,
  UserMinus,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { ActivityAction, ActivityEntityType, ActivityLogEntry } from '@repo/types';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useActivity } from '@/hooks/useActivity';
import { cn } from '@/lib/utils';

// Icon + colour tone per action. Falls back to a per-entity icon so a new action
// value still renders something sensible.
const ACTION_PRESENTATION: Record<
  ActivityAction,
  { icon: LucideIcon; tone: string }
> = {
  created: { icon: CheckCircle2, tone: 'text-emerald-600 bg-emerald-600/10 dark:text-emerald-500' },
  updated: { icon: Pencil, tone: 'text-chart-1 bg-primary/10' },
  deleted: { icon: Trash2, tone: 'text-destructive bg-destructive/10' },
  status_changed: { icon: RefreshCw, tone: 'text-amber-600 bg-amber-600/10 dark:text-amber-500' },
  bulk_status_changed: { icon: Layers, tone: 'text-amber-600 bg-amber-600/10 dark:text-amber-500' },
  deactivated: { icon: UserMinus, tone: 'text-destructive bg-destructive/10' },
  scraped: { icon: Briefcase, tone: 'text-chart-1 bg-primary/10' },
  assigned: { icon: UserCheck, tone: 'text-chart-1 bg-primary/10' },
  replied: { icon: MessageSquare, tone: 'text-chart-1 bg-primary/10' },
};

const ENTITY_ICON: Record<ActivityEntityType, LucideIcon> = {
  job: Briefcase,
  customer: UserPlus,
  employee: UserCog,
  scrape: Briefcase,
  support_ticket: LifeBuoy,
};

const ENTITY_NOUN: Record<ActivityEntityType, string> = {
  job: 'job',
  customer: 'customer',
  employee: 'employee',
  scrape: 'scrape',
  support_ticket: 'ticket',
};

/** Build a human sentence for a feed row, e.g. `Someone created job “…”`. */
function describe(item: ActivityLogEntry): string {
  const who = item.actorName ?? 'Someone';
  const noun = ENTITY_NOUN[item.entityType] ?? 'item';
  const label = item.entityLabel ? `“${item.entityLabel}”` : null;
  const status = typeof item.metadata?.status === 'string' ? item.metadata.status : null;

  switch (item.action) {
    case 'created':
      return `${who} created ${noun} ${label ?? ''}`.trim();
    case 'updated':
      return `${who} updated ${noun} ${label ?? ''}`.trim();
    case 'deleted':
      return `${who} deleted ${noun} ${label ?? ''}`.trim();
    case 'deactivated':
      return `${who} deactivated ${noun} ${label ?? ''}`.trim();
    case 'status_changed':
      return `${who} set ${label ?? noun}${status ? ` to ${status}` : ''}`;
    case 'bulk_status_changed':
      // entityLabel already reads like "5 jobs" for bulk actions.
      return `${who} set ${item.entityLabel ?? 'several jobs'}${status ? ` to ${status}` : ''}`;
    case 'scraped':
      return `${who} scraped ${noun} ${label ?? ''}`.trim();
    case 'assigned':
      return `${who} assigned ${noun} ${label ?? ''}`.trim();
    case 'replied':
      return `${who} replied to ${noun} ${label ?? ''}`.trim();
    default:
      return `${who} updated ${noun} ${label ?? ''}`.trim();
  }
}

/** Relative time for a row, guarded against an unparseable timestamp. */
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true });
}

function presentationFor(item: ActivityLogEntry): { icon: LucideIcon; tone: string } {
  return (
    ACTION_PRESENTATION[item.action] ?? {
      icon: ENTITY_ICON[item.entityType] ?? Briefcase,
      tone: 'text-muted-foreground bg-muted',
    }
  );
}

// Height of the soft fade at the scroll edges.
const FADE = '28px';

export function RecentActivity() {
  // Cap at the 10 most recent; the list scrolls within the card.
  const { data, isLoading, isError } = useActivity({ limit: 10 });
  const items = data?.data ?? [];

  // Track whether there's hidden content above/below so we only fade the edge
  // that actually has more to scroll toward.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);

  // Recompute after the content renders and whenever the viewport resizes.
  useEffect(() => {
    updateFade();
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [updateFade, items.length, isLoading, isError]);

  const maskImage = `linear-gradient(to bottom, ${fade.top ? 'transparent' : '#000'} 0, #000 ${FADE}, #000 calc(100% - ${FADE}), ${fade.bottom ? 'transparent' : '#000'} 100%)`;

  return (
    // Fixed height on desktop so the list scrolls *inside* the card (matching
    // the Scraping Pipeline card beside it) instead of growing the row; natural
    // height on mobile where the cards stack.
    <Card className="flex flex-col lg:h-[340px]">
      <CardHeader>
        <CardTitle>Recent Activity</CardTitle>
        <CardDescription>Latest events across the platform</CardDescription>
      </CardHeader>
      {/* min-h-0 allows the body to shrink below its content so it scrolls
          instead of forcing the card taller than the pipeline card. The mask
          softly fades the top/bottom edges when there's more to scroll. */}
      <CardContent
        ref={scrollRef}
        onScroll={updateFade}
        style={{ maskImage, WebkitMaskImage: maskImage }}
        className="min-h-0 flex-1 overflow-y-auto"
      >
        {isLoading ? (
          <ul className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="flex items-start gap-3">
                <Skeleton className="size-8 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </li>
            ))}
          </ul>
        ) : isError ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Couldn’t load recent activity.
          </p>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <ul className="space-y-4">
            {items.map((item) => {
              const { icon: Icon, tone } = presentationFor(item);
              return (
                <li key={item.id} className="flex items-start gap-3">
                  <span
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center rounded-full',
                      tone
                    )}
                  >
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* Clamp to 2 lines — some scraped job titles are long blobs. */}
                    <p className="line-clamp-2 text-sm leading-snug">{describe(item)}</p>
                    <p className="text-xs text-muted-foreground">
                      {relativeTime(item.createdAt)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
